/**
 * Convert assistant HTML (already link-formatted) to readable plain text for clipboard.
 * No Markdown — uses Unicode bullets, numbered lists, clear headings, and
 * "Label (url)" for links so pastes look neat in any app.
 */

function normalizeWhitespace(s: string): string {
  return s.replace(/\n{3,}/g, '\n\n').trim();
}

function walkInline(node: ChildNode): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? '';
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return '';
  }
  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();

  switch (tag) {
    case 'br':
      return '\n';
    case 'a': {
      const href = el.getAttribute('href')?.trim() ?? '';
      const label = Array.from(el.childNodes).map(walkInline).join('').trim();
      if (!href) return label;
      if (!label || label === href) return href;
      return `${label} (${href})`;
    }
    case 'strong':
    case 'b':
    case 'em':
    case 'i':
    case 'span':
    case 'small':
    case 'mark':
    case 'code':
    case 'kbd':
    case 'sup':
    case 'sub':
      return Array.from(el.childNodes).map(walkInline).join('');
    default:
      return Array.from(el.childNodes).map(walkInline).join('');
  }
}

function walkBlock(node: ChildNode): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? '';
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return '';
  }
  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();

  switch (tag) {
    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
    case 'h6': {
      const t = Array.from(el.childNodes).map(walkInline).join('').trim();
      return t ? `\n\n${t}\n` : '';
    }
    case 'p':
      return `\n${Array.from(el.childNodes).map(walkBlock).join('').trim()}\n`;
    case 'br':
      return '\n';
    case 'hr':
      return '\n---\n';
    case 'blockquote':
      return `\n${Array.from(el.childNodes)
        .map(walkBlock)
        .join('')
        .split('\n')
        .map((line) => (line.trim() ? `  ${line}` : line))
        .join('\n')}\n`;
    case 'ul':
      return `\n${formatUl(el)}\n`;
    case 'ol':
      return `\n${formatOl(el)}\n`;
    case 'li':
      // Usually handled by ul/ol; if orphaned, treat as line
      return `${Array.from(el.childNodes).map(walkBlock).join('').trim()}\n`;
    case 'div':
    case 'section':
    case 'article':
    case 'main':
      return Array.from(el.childNodes).map(walkBlock).join('');
    case 'pre': {
      const t = el.textContent ?? '';
      return `\n${t}\n`;
    }
    case 'table':
    case 'thead':
    case 'tbody':
    case 'tfoot': {
      return `\n${Array.from(el.querySelectorAll('tr'))
        .map((tr) =>
          Array.from(tr.querySelectorAll('th, td'))
            .map((cell) => (cell.textContent ?? '').replace(/\s+/g, ' ').trim())
            .join('\t'),
        )
        .filter(Boolean)
        .join('\n')}\n`;
    }
    case 'tr':
    case 'td':
    case 'th':
      // Usually handled via table; fall through
      return Array.from(el.childNodes).map(walkBlock).join('');
    default:
      return Array.from(el.childNodes).map(walkBlock).join('');
  }
}

function liBody(li: HTMLElement): string {
  return Array.from(li.childNodes).map(walkBlock).join('').replace(/\n+$/g, '').trim();
}

function formatUl(ul: HTMLElement): string {
  const items = Array.from(ul.children).filter((c) => c.tagName === 'LI') as HTMLElement[];
  return items.map((li) => `• ${liBody(li)}\n`).join('');
}

function formatOl(ol: HTMLElement): string {
  const items = Array.from(ol.children).filter((c) => c.tagName === 'LI') as HTMLElement[];
  return items.map((li, i) => `${i + 1}. ${liBody(li)}\n`).join('');
}

/**
 * @param html - HTML string (e.g. after formatAllLinks)
 */
export function htmlToPrettyPlainText(html: string): string {
  if (!html || !html.trim()) return '';

  const div = document.createElement('div');
  div.innerHTML = html;

  const out = Array.from(div.childNodes).map(walkBlock).join('');
  return normalizeWhitespace(out);
}
